import { createContext, useContext, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * Minimal controlled tabs — Radix Tabs is not installed and a single
 * two/three-tab switch does not justify a dependency. The active value is
 * owned by the parent, keeping the component a pure controlled primitive.
 */
interface TabsContextValue {
  value: string;
  onValueChange: (value: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabs(): TabsContextValue {
  const ctx = useContext(TabsContext);

  if (!ctx) throw new Error('Tabs subcomponents must be used within <Tabs>');

  return ctx;
}

export function Tabs({
  value,
  onValueChange,
  className,
  children,
}: {
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <TabsContext.Provider value={{ value, onValueChange }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

export function TabsList({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      role="tablist"
      className={cn(
        'inline-flex h-9 items-center justify-center gap-1 rounded-md bg-muted p-1 text-muted-foreground',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function TabsTrigger({
  value,
  children,
}: {
  value: string;
  children: ReactNode;
}): JSX.Element {
  const tabs = useTabs();
  const active = tabs.value === value;

  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={() => tabs.onValueChange(value)}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded px-3 py-1 text-sm font-medium transition-colors',
        active
          ? 'bg-background text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

export function TabsContent({
  value,
  className,
  children,
}: {
  value: string;
  className?: string;
  children: ReactNode;
}): JSX.Element | null {
  const tabs = useTabs();

  if (tabs.value !== value) return null;

  return (
    <div role="tabpanel" className={className}>
      {children}
    </div>
  );
}
